/**
 * Event detail completeness (Oct 2026).
 *
 * Kitchen and operations need every booking fully specified. Rules agreed
 * with Ronan:
 *  - A booking more than 7 days out may be saved with missing details. It
 *    shows ORANGE ("Provisional") and reception gets a task, due 7 days
 *    before the event, to complete it.
 *  - Once inside 7 days, an incomplete booking shows RED until done.
 *  - A NEW booking inside 7 days (or one moved into the window) cannot be
 *    saved until every required field is filled.
 *  - Edits to an event already inside the window may be saved partially;
 *    it simply stays red.
 *  - Complete = GREEN ("Booked").
 *
 *  - "Music booking only" tick (musicOnly): no required fields at all, so
 *    it is never orange/red, never blocked, and gets no reception task.
 *
 * Status is always computed from the fields, never stored, so it can't go
 * stale. Works on both stored event objects (name/client/clientPhone…) and
 * the add/edit form objects (eventName/clientName/contactPhone…).
 */
import { todayKey, parseLocalDate } from './dateHelpers';

export const DETAIL_TASK_TYPE = 'complete_details';
export const DETAIL_WINDOW_DAYS = 7;

// Stages that are not live bookings — no colour, no task, no save block.
const INACTIVE_STAGES = ['archived', 'lost', 'cancelled', 'complete'];

const first = (e, ...keys) => {
  for (const k of keys) {
    if (e[k] !== undefined && e[k] !== null) return e[k];
  }
  return undefined;
};
const filled = (v) => typeof v === 'number' ? true : !!String(v ?? '').trim();
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
// A dietary count counts as answered when it holds a number — 0 included.
// Blank means "not asked yet".
const answeredCount = (v) => v !== '' && v !== null && v !== undefined && !Number.isNaN(parseInt(v, 10));

export function isDrinksOnly(e) {
  return e?.foodServiceTime === 'none';
}

export function hasDietaryAnswer(e) {
  // 'NA' tick = no meal chosen, dietary not applicable.
  return !!e.dietaryNA || answeredCount(e.dietaryVeg) || answeredCount(e.dietaryVegan) || answeredCount(e.dietaryCoeliac)
    || filled(e.dietaryAllergens) || filled(e.dietaryRequirements);
}

/** List of required fields still missing: [{ key, label, tab }]. */
export function isMusicOnly(e) {
  return !!e?.musicOnly;
}

export function getMissingEventFields(e) {
  if (!e) return [];
  // Music-only bookings: nothing is mandatory.
  if (isMusicOnly(e)) return [];
  const missing = [];
  const add = (key, label, tab) => missing.push({ key, label, tab });
  const drinksOnly = isDrinksOnly(e);

  if (!filled(first(e, 'name', 'eventName'))) add('name', 'Event name', 'details');
  if (!filled(first(e, 'client', 'clientName'))) add('client', 'Client name', 'details');
  if (!filled(first(e, 'clientPhone', 'contactPhone')) && !filled(first(e, 'clientEmail', 'contactEmail'))) add('contact', 'Phone or email', 'details');
  if (!filled(e.type)) add('type', 'Event type', 'details');
  if (!filled(e.date)) add('date', 'Date', 'details');
  if (!filled(first(e, 'venue_id', 'venueId'))) add('venue', 'Venue', 'details');
  if (!(num(e.guests) > 0)) add('guests', 'Guest numbers', 'details');
  if (!filled(e.startTime)) add('startTime', 'Start time', 'details');
  if (!filled(e.endTime)) add('endTime', 'End time', 'details');
  if (!filled(e.setup)) add('setup', 'Setup style', 'setup');
  if (!filled(e.setupRequirements)) add('setupRequirements', 'Setup requirements', 'setup');
  if (!drinksOnly) {
    if (!filled(effectiveFoodTime(e))) add('foodServiceTime', 'Food service time (or tick Drinks only)', 'setup');
    if (!hasDietaryAnswer(e)) add('dietary', 'Dietary needs (or tick No dietary requirements / NA)', 'setup');
  }
  const priced = num(e.pricePerHead) > 0 || num(e.packageTotal) > 0
    || (drinksOnly && (num(e.roomHire) > 0 || num(e.consumption) > 0));
  if (!priced) add('pricing', drinksOnly ? 'Pricing (per head, package, room hire or bar minimum)' : 'Pricing (per head or package)', 'financials');
  return missing;
}

/** Whole days from today until the event starts (negative = started). */
export function daysUntil(dateKey, today = todayKey()) {
  const a = parseLocalDate(today);
  const b = parseLocalDate(dateKey);
  if (!a || !b) return null;
  return Math.round((b - a) / 86400000);
}

/**
 * status: 'booked' (green) | 'music' (purple) | 'provisional' (orange) | 'urgent' (red) | 'none'
 * 'none' = not a live upcoming booking (past, archived, cancelled, no date).
 */
export function getEventDetailStatus(e, today = todayKey()) {
  if (!e || !e.date) return { status: 'none', missing: [], days: null };
  if (INACTIVE_STAGES.includes(e.stage)) return { status: 'none', missing: [], days: null };
  const lastDay = e.endDate || e.date;
  if (lastDay < today) return { status: 'none', missing: [], days: null };
  const days = Math.max(0, daysUntil(e.date, today) ?? 0);
  if (isMusicOnly(e)) return { status: 'music', missing: [], days };
  const missing = getMissingEventFields(e);
  if (!missing.length) return { status: 'booked', missing, days };
  return { status: days <= DETAIL_WINDOW_DAYS ? 'urgent' : 'provisional', missing, days };
}

/**
 * Returns an error message if this save must be blocked, otherwise null.
 * `original` is the stored event for an edit, or null for a new booking.
 */
export function getDetailSaveBlock(formEvt, original, today = todayKey()) {
  const { status, missing } = getEventDetailStatus(formEvt, today);
  if (status !== 'urgent') return null;
  if (original && original.date) {
    const origDays = daysUntil(original.date, today);
    const origLive = !INACTIVE_STAGES.includes(original.stage);
    // Already inside the window before this edit → partial save allowed (stays red).
    if (origLive && origDays !== null && origDays <= DETAIL_WINDOW_DAYS) return null;
  }
  const why = original
    ? 'This event is now within 7 days, so every detail must be filled in before saving.'
    : 'This booking is within 7 days, so every detail must be filled in before it can be saved.';
  return `${why} Missing: ${missing.map((m) => m.label).join(', ')}.`;
}

export const DETAIL_STATUS_STYLES = {
  booked: {
    label: 'Booked',
    long: 'Booked – details complete',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    border: 'border-l-emerald-500',
    hex: '#10B981',
  },
  music: {
    label: 'Music',
    long: 'Music booking only – no details needed',
    dot: 'bg-violet-500',
    badge: 'bg-violet-100 text-violet-800 border-violet-300',
    border: 'border-l-violet-500',
    hex: '#8B5CF6',
  },
  provisional: {
    label: 'Provisional',
    long: 'Provisional – details needed',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800 border-orange-300',
    border: 'border-l-orange-500',
    hex: '#F97316',
  },
  urgent: {
    label: 'Under a week',
    long: 'Provisional – under a week, details needed now',
    dot: 'bg-red-600',
    badge: 'bg-red-100 text-red-800 border-red-300',
    border: 'border-l-red-600',
    hex: '#DC2626',
  },
};

/** Due date for the reception task: 7 days before the event, never earlier than today. */
export function detailTaskDueDate(dateKey, today = todayKey()) {
  const d = parseLocalDate(dateKey);
  if (!d) return today;
  d.setDate(d.getDate() - DETAIL_WINDOW_DAYS);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const key = `${y}-${m}-${day}`;
  return key < today ? today : key;
}

// Red box around required inputs that are still empty (forms).
export const MISSING_INPUT_CLS = ' !border-red-500 !bg-red-50 ring-1 ring-red-400';

/** Returns (key) => extra className — red box when that required field is missing. */
// Not for archived/cancelled or already-finished events (nothing to fix there).
const formApplies = (e, today = todayKey()) => !!e && !INACTIVE_STAGES.includes(e.stage)
  && !(e.date && (e.endDate || e.date) < today);

export function missingFieldClass(e) {
  const keys = new Set(formApplies(e) ? getMissingEventFields(e).map((m) => m.key) : []);
  return (key) => (keys.has(key) ? MISSING_INPUT_CLS : '');
}

/** Number of missing required fields per form tab: { details: 2, setup: 1, ... } */
export function missingCountByTab(e) {
  if (!formApplies(e)) return {};
  return getMissingEventFields(e).reduce((acc, m) => { acc[m.tab] = (acc[m.tab] || 0) + 1; return acc; }, {});
}

// ─── Tick conflicts (30 Oct 2026) ───────────────────────────────────────────
// ESB Network (30 Oct) had "Drinks only" ticked while carrying a 12:45 soup &
// mains service line for 50 — so the kitchen view left it out of food. When a
// tick says "no food" but the booking's own details say otherwise, we trust
// the details: the event stays a full food event on sheets/kitchen, flagged.
//  - STRONG evidence (changes what the kitchen sees): a service line with a
//    time or description, or a preset F&B item. For music-only, a price per
//    head / package total also counts (music bookings aren't priced per head).
//  - SOFT evidence (form warning only): food words in notes/dietary text.
//    Notes are free text ("part 2 of the hen party, dinner in R&R") so they
//    never change sheets, they just prompt reception to double-check.
const FOOD_WORDS = /\b(soup|starters?|mains?|dinner|lunch|breakfast|brunch|buffet|carvery|scones?|sandwich(?:es)?|courses?|desserts?|canap[eé]s?|finger food|bbq|barbecue|platters?|afternoon tea)\b/i;

function serviceLines(e) {
  const svc = Array.isArray(e?.services) ? e.services : [];
  const out = svc
    .filter((s) => s && (String(s.description || '').trim() || String(s.time || '').trim()))
    .map((s) => ({ time: String(s.time || '').trim(), description: String(s.description || '').trim() }));
  (Array.isArray(e?.fbItems) ? e.fbItems : []).forEach((it) => {
    if (it) out.push({ time: '', description: typeof it === 'string' ? it : (it.name || it.label || 'F&B item') });
  });
  return out;
}

/** Earliest time on the event's service timeline (HH:MM), or ''. */
export function earliestServiceTime(e) {
  const times = serviceLines(e).map((l) => l.time).filter((t) => /^\d{1,2}:\d{2}/.test(t))
    .map((t) => t.padStart(5, '0').slice(0, 5)).sort();
  return times[0] || '';
}

/** Food time the kitchen should use: explicit value, else timeline, else ''. 'none' = drinks only. */
export function effectiveFoodTime(e) {
  if (!e) return '';
  if (e.foodServiceTime === 'none') return 'none';
  return e.foodServiceTime || earliestServiceTime(e);
}

function foodEvidence(e) {
  const lines = serviceLines(e);
  const strong = lines.length
    ? [`it has ${lines.length === 1 ? 'a service line' : `${lines.length} service lines`} (${lines.map((l) => [l.time, l.description].filter(Boolean).join(' ')).join('; ')})`]
    : [];
  const text = [e?.notes, e?.dietaryRequirements].filter(Boolean).join(' ')
    .replace(/\b(no|without)\s+food\b/gi, '');
  const m = text.match(FOOD_WORDS);
  const soft = m ? [`the notes mention "${m[0]}"`] : [];
  return { strong, soft, firstTime: (lines.find((l) => l.time) || {}).time || '' };
}

/**
 * { strong: [msg], soft: [msg], foodTime } — strong conflicts change how the
 * event is shown (kept as a food event); soft ones are form warnings only.
 */
export function getTickConflicts(e) {
  const res = { strong: [], soft: [], foodTime: '' };
  if (!e) return res;
  const ev = foodEvidence(e);
  if (isDrinksOnly(e)) {
    ev.strong.forEach((s) => res.strong.push(`"Drinks only" is ticked but ${s}`));
    ev.soft.forEach((s) => res.soft.push(`"Drinks only" is ticked but ${s}`));
  }
  if (isMusicOnly(e)) {
    ev.strong.forEach((s) => res.strong.push(`"Music booking only" is ticked but ${s}`));
    if (num(first(e, 'pricePerHead')) > 0 || num(first(e, 'packageTotal')) > 0) {
      res.strong.push('"Music booking only" is ticked but it has a price per head / package');
    }
    ev.soft.forEach((s) => res.soft.push(`"Music booking only" is ticked but ${s}`));
  }
  res.foodTime = ev.firstTime;
  return res;
}

/**
 * Why this event will look different on the printed daily sheet / Kitchen
 * Live than in the full list. [{ tone: 'warn'|'info', text }]. Used on the
 * on-screen Function Sheets cards so "it's on the weekly but not the kitchen"
 * is never a mystery.
 */
const SHEET_HIDDEN_STAGES = ['archived', 'lost', 'cancelled'];
export function getOpsVisibilityNotes(e) {
  const out = [];
  if (!e) return out;
  if (SHEET_HIDDEN_STAGES.includes(e.stage)) {
    out.push({ tone: 'warn', text: `Status is "${e.stage}" – this event is NOT on the printed daily sheet or Kitchen Live (shown there only as "Not running"). Change the status if it is going ahead.` });
    return out;
  }
  const c = getTickConflicts(e);
  c.strong.forEach((t) => out.push({ tone: 'warn', text: `${t}. Kitchen and daily sheet are treating it as a food event – please fix the tick.` }));
  if (!c.strong.length) {
    if (isMusicOnly(e) && !(e.foodServiceTime && e.foodServiceTime !== 'none')) {
      out.push({ tone: 'info', text: 'Music booking only – daily sheet and kitchen show it as one small line, not as a function.' });
    } else if (isDrinksOnly(e)) {
      out.push({ tone: 'info', text: 'Drinks only – left out of kitchen food covers.' });
    }
    c.soft.forEach((t) => out.push({ tone: 'warn', text: `${t} – is that right?` }));
  }
  return out;
}
